import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEO Component for Harmonie Féminine par Nutri Centre Riviera.
 * Props: title, description, keywords, image, url, type, author, jsonLd.
 * Note: the LocalBusiness JSON-LD is injected globally by LocalBusinessSchema.jsx (single source of truth).
 */

const SEO = ({
  title = "Harmonie Féminine — Nutritionniste Vevey, Clarens & Montreux",
  description = "Centre pluridisciplinaire de nutrition et santé féminine sur la Riviera vaudoise. Nutrition hormonale, perte de poids, SOPK, préménopause, fertilité, rééquilibrage alimentaire. Vevey, Clarens, Montreux — canton de Vaud.",
  keywords = "nutritionniste Vevey, nutritionniste Clarens, nutritionniste Montreux, nutritionniste Lausanne, nutrition hormonale, perte de poids Riviera, SOPK Vaud, préménopause, ménopause, fertilité, nutrition féminine, rééquilibrage alimentaire, hypnopraxie, compulsions alimentaires, rétention d'eau, canton de Vaud",
  image = "https://nutricentreriviera.ch/images/hero-home.webp",
  url = "https://nutricentreriviera.ch",
  type = "website",
  author = "Nutri Centre Riviera",
  jsonLd = null
}) => {
  const extraJsonLds = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  // Auto BreadcrumbList (skipped when the page already provides one)
  const hasBreadcrumb = extraJsonLds.some((d) => d && d["@type"] === "BreadcrumbList");
  let autoBreadcrumb = null;
  if (!hasBreadcrumb) {
    try {
      const segs = new URL(url).pathname.split("/").filter(Boolean);
      if (segs.length) {
        const items = [{ name: "Accueil", url: "https://nutricentreriviera.ch/" }];
        if (segs.length > 1 && segs[0] === "blog") {
          items.push({ name: "Blog", url: "https://nutricentreriviera.ch/blog" });
        }
        items.push({ name: title.split(/\s+[—|]\s+/)[0].trim(), url });
        autoBreadcrumb = buildBreadcrumbJsonLd(items);
      }
    } catch (e) {
      autoBreadcrumb = null;
    }
  }
  const allJsonLds = autoBreadcrumb ? [...extraJsonLds, autoBreadcrumb] : extraJsonLds;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content="fr_CH" />
      <meta property="og:site_name" content="Harmonie Féminine par Nutri Centre Riviera" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={url} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={image} />

      {/* Geo Tags for Local SEO */}
      <meta name="geo.region" content="CH-VD" />
      <meta name="geo.placename" content="Vevey, Clarens, Montreux" />
      <meta name="geo.position" content="46.4608;6.8430" />
      <meta name="ICBM" content="46.4608, 6.8430" />

      {/* Language & hreflang for Switzerland */}
      <meta httpEquiv="content-language" content="fr-CH" />
      <link rel="alternate" hrefLang="fr-CH" href={url} />
      <link rel="alternate" hrefLang="fr" href={url} />
      <link rel="alternate" hrefLang="x-default" href={url} />

      {/* Canonical */}
      <link rel="canonical" href={url} />

      {/* Optional extra JSON-LD (FAQ, Article, Breadcrumb, etc.) */}
      {allJsonLds.map((data, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
};

// ----------------- JSON-LD helper builders -----------------

export const buildFAQPageJsonLd = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map((f) => ({
    "@type": "Question",
    "name": f.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": f.answer
    }
  }))
});

export const buildBreadcrumbJsonLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, idx) => ({
    "@type": "ListItem",
    "position": idx + 1,
    "name": item.name,
    "item": item.url
  }))
});

export const buildArticleJsonLd = ({ title, description, image, datePublished, dateModified, url, author = "Harmonie Féminine" }) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": title,
  "description": description,
  "image": image,
  "datePublished": datePublished,
  "dateModified": dateModified || datePublished,
  "author": {
    "@type": "Organization",
    "name": author,
    "url": "https://nutricentreriviera.ch"
  },
  "publisher": {
    "@type": "Organization",
    "name": "Harmonie Féminine par Nutri Centre Riviera",
    "url": "https://nutricentreriviera.ch"
  },
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": url
  }
});

export default SEO;
